import { BusinessListingResult, BusinessListingForm } from '../../data/types/application';
import { Business, ListingPolicy, ServiceLocation, ShippingPolicy } from '../../data/types/domain';
import { businessService } from '../../services/businessService';
import { listingPolicyService } from '../../services/listingPolicyService';
import { serviceLocationService } from '../../services/serviceLocationService';
import { shippingPolicyService } from '../../services/shippingPolicyService';
import { usecase } from './decorators';

interface BusinessInput {
  isEditMode?: boolean;
  business_id?: number;
  businessListing: BusinessListingForm;
}

const removeIds = <T extends object>(obj: T): Partial<T> => {
  const newObj = { ...obj };
  delete (newObj as any).id;
  delete (newObj as any).created_at;
  delete (newObj as any).updated_at;
  delete (newObj as any).business_id;
  return newObj;
};


/**
 * Creates a new business with associated policies
 */
export const createOrUpdateBusinessListing = usecase<BusinessInput, BusinessListingResult>(
  'createOrUpdateBusinessListing',
  {
    retryCount: 0,
    logLevel: 'info',
    validateInput: (input) => {
      return !!input.businessListing.listingPolicy && 
             Array.isArray(input.businessListing.shippingPolicies) && 
             input.businessListing.shippingPolicies.length > 0 &&
             Array.isArray(input.businessListing.serviceLocations) && 
             input.businessListing.serviceLocations.length > 0;
    },
    onError: (error, input) => {
      console.error('Failed to create business:', error.message);
      // You could send this to an error tracking service
    }
  }
)(async (input: BusinessInput): Promise<BusinessListingResult> => {
  // Create the business first
  if (input.isEditMode && !input.business_id) {
    throw new Error('Business ID is required for update');
  }

  // Update the business and associated policies
  if (input.isEditMode && input.business_id) {
    const business = await businessService.updateBusiness(input.business_id, input.businessListing.business);
    const listingPolicy = await listingPolicyService.updateListingPolicy(input.business_id, removeIds(input.businessListing.listingPolicy));
  
    // Remove all existing shipping policies and create new ones 
    await shippingPolicyService.deleteShippingPolicies(input.business_id!);
    const shippingPolicies = await Promise.all(
      input.businessListing.shippingPolicies.map(policy => {
        const validatedPolicy = {
          from_country: policy.from_country,
          to_country: policy.to_country,
          minimum_order_type: policy.minimum_order_type,
          currency: policy.currency,
          minimum_order_amount: policy.minimum_order_amount,
          minimum_per_kg_price: policy.minimum_per_kg_price,
          business_id: input.business_id!
        };
        return shippingPolicyService.createShippingPolicy(validatedPolicy);
      })
    );

    // Remove all existing service locations and create new ones
    await serviceLocationService.deleteServiceLocations(input.business_id!);
    const serviceLocations = await Promise.all(
      input.businessListing.serviceLocations.map(location => {
        const validatedLocation = {
          country: location.country,
          city: location.city,
          business_id: input.business_id!
        };
        return serviceLocationService.createServiceLocation(validatedLocation);
      })
    );

    return {
      business,
      listingPolicy,
      shippingPolicies,
      serviceLocations
    };

  } else {

    // Create the business and associated policies
    const business = await businessService.createBusiness(input.businessListing);
    const listingPolicy = await listingPolicyService.createListingPolicy({
      ...input.businessListing.listingPolicy,
      business_id: business.id
    });
    const shippingPolicies = await Promise.all(
      input.businessListing.shippingPolicies.map(policy => 
        shippingPolicyService.createShippingPolicy({
          ...policy,  
          business_id: business.id
        })
      )
    );
    const serviceLocations = await Promise.all(
      input.businessListing.serviceLocations.map(location => {
        if (!location.country) {
          throw new Error('Missing country field in service location');
        }
        const validatedLocation = {
          country: location.country,
          city: location.city,
          business_id: business.id
        };
        return serviceLocationService.createServiceLocation(validatedLocation);
      })
    );

    return {
      business,
      listingPolicy,
      shippingPolicies,
      serviceLocations
    };
  }
});

/**
 * Deletes a business listing and all associated policies and locations
 */
export const deleteBusinessListing = usecase<{ business_id: number }, void>(
  'deleteBusinessListing',
  {
    retryCount: 0,
    logLevel: 'info',
    validateInput: (input) => {
      return !!input.business_id;
    },
    onError: (error, input) => {
      console.error('Failed to delete business:', error.message);
      // You could send this to an error tracking service
    }
  }
)(async (input: { business_id: number }): Promise<void> => {
  // Delete in reverse order of creation to maintain referential integrity
  await shippingPolicyService.deleteShippingPolicies(input.business_id);
  await serviceLocationService.deleteServiceLocations(input.business_id);
  await listingPolicyService.deleteListingPolicy(input.business_id);
  await businessService.deleteBusiness(input.business_id);
});